// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Badge, Stack, Text, Title } from '@mantine/core';
import type { PractitionerRole } from '@medplum/fhirtypes';
import { DataTable } from 'mantine-datatable';
import { type JSX, useMemo } from 'react';
import { POLARIS_PROVIDER_ROLES } from '../../constants/appointmentTypes';
import { countPermissionsForRole, ROLES, type Role } from '../../utils/permissions';
import { countRoleAssignments, EMPTY_VALUE } from './staffing-utils';

interface AccountTypesTableProps {
  practitionerRoles: PractitionerRole[];
}

interface AccountTypeRow {
  code: Role;
  name: string;
  numberActive: number;
  permissionsCount: number;
}

const ROLE_DISPLAY: Record<Role, string> = {
  physician: POLARIS_PROVIDER_ROLES.physician.display,
  'nurse-practitioner': POLARIS_PROVIDER_ROLES['nurse-practitioner'].display,
  'care-coordinator': POLARIS_PROVIDER_ROLES['care-coordinator'].display,
  'software-engineer': POLARIS_PROVIDER_ROLES['software-engineer'].display,
  'clinical-ops-rcm': 'Clinical Ops / RCM',
};

export function AccountTypesTable({ practitionerRoles }: AccountTypesTableProps): JSX.Element {
  const rows = useMemo<AccountTypeRow[]>(
    () =>
      Object.values(ROLES).map((code) => ({
        code,
        name: ROLE_DISPLAY[code],
        numberActive: countRoleAssignments(practitionerRoles, code),
        permissionsCount: countPermissionsForRole(code),
      })),
    [practitionerRoles]
  );

  return (
    <Stack gap="md">
      <Title order={3}>Account Types</Title>

      <DataTable
        minHeight={150}
        records={rows}
        idAccessor="code"
        striped
        highlightOnHover
        fz="sm"
        noRecordsText="No account types."
        columns={[
          { accessor: 'name', title: 'Name' },
          {
            accessor: 'status',
            title: 'Status',
            render: () => (
              <Badge color="green" variant="light">
                Active
              </Badge>
            ),
          },
          { accessor: 'numberActive', title: 'Number Active' },
          { accessor: 'permissionsCount', title: 'Permissions' },
          {
            accessor: 'lastEdited',
            title: 'Last Edited',
            render: () => (
              <Text size="sm" c="dimmed">
                {EMPTY_VALUE}
              </Text>
            ),
          },
        ]}
      />
    </Stack>
  );
}
